import { Check } from 'lucide-react';
import translations from '@/data/translations.json';
import { useSettings } from '@/lib/store';
import type { ThemePref } from '@/lib/types';
import { Range, Row, Section, Segmented, Sheet, Switch } from '@/components/ui';
import { MUSHAFS, isGlyphMushaf } from './fonts';

const TRANSLATIONS = translations as { id: number; name: string; author: string; lang: string }[];

export function MushafPicker() {
  const mushaf = useSettings((s) => s.settings.mushaf);
  const update = useSettings((s) => s.update);
  return (
    <Section title="Mushaf" footer="Page fonts download as you read and are kept for offline use.">
      {MUSHAFS.map((m) => (
        <Row key={m.id} label={<span><span className="block">{m.name}</span><span className="block text-[13px] text-muted">{m.desc}</span></span>} onClick={() => update({ mushaf: m.id })}>
          {mushaf === m.id ? <Check size={20} className="text-accent" /> : <span className="w-5" />}
        </Row>
      ))}
    </Section>
  );
}

export function TranslationSelect() {
  const id = useSettings((s) => s.settings.translationId);
  const update = useSettings((s) => s.update);
  const langs = [...new Set(TRANSLATIONS.map((t) => t.lang))];
  return (
    <select aria-label="Translation" value={id} onChange={(e) => update({ translationId: Number(e.target.value) })}
      className="max-w-[60%] rounded-lg bg-fill px-2 py-1.5 text-[15px] text-ink">
      {langs.map((l) => (
        <optgroup key={l} label={l[0].toUpperCase() + l.slice(1)}>
          {TRANSLATIONS.filter((t) => t.lang === l).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

export function ReaderSettingsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const s = useSettings((st) => st.settings);
  const update = useSettings((st) => st.update);
  return (
    <Sheet open={open} onClose={onClose} title="Reading">
      <div className="px-4 pb-4">
        <Segmented label="Theme" value={s.theme} onChange={(v: ThemePref) => update({ theme: v })}
          options={[{ value: 'system', label: 'Auto' }, { value: 'light', label: 'Light' }, { value: 'sepia', label: 'Sepia' }, { value: 'dark', label: 'Dark' }]} />
      </div>
      <div className="px-4 pb-4">
        <Segmented label="Reading mode" value={s.readingMode} onChange={(v) => update({ readingMode: v })}
          options={[{ value: 'page', label: 'Mushaf page' }, { value: 'list', label: 'Āyah by āyah' }]} />
      </div>
      <MushafPicker />
      <Section title="Display">
        {(!isGlyphMushaf(s.mushaf) || s.readingMode === 'list') && (
          <div className="row flex-col !items-stretch">
            <div className="flex justify-between text-[15px]"><span>Arabic size</span><span className="text-muted">{Math.round(s.arabicSize * 100)}%</span></div>
            <Range label="Arabic size" min={0.7} max={2} step={0.05} value={s.arabicSize} onChange={(v) => update({ arabicSize: v })} />
          </div>
        )}
        <Row label="Translation"><Switch label="Show translation" checked={s.showTranslation} onChange={(v) => update({ showTranslation: v })} /></Row>
        {s.showTranslation && <Row label="Translator"><TranslationSelect /></Row>}
        <Row label="Transliteration"><Switch label="Show transliteration" checked={s.showTransliteration} onChange={(v) => update({ showTransliteration: v })} /></Row>
        <Row label="Word-by-word meanings"><Switch label="Word by word" checked={s.wordByWord} onChange={(v) => update({ wordByWord: v })} /></Row>
        <Row label="Follow the reciter"><Switch label="Follow audio" checked={s.followAudio} onChange={(v) => update({ followAudio: v })} /></Row>
      </Section>
      <p className="px-8 text-[13px] text-muted">Translation, transliteration and word-by-word appear in Āyah-by-āyah mode.</p>
    </Sheet>
  );
}
